import React, { useEffect, useState } from 'react';
import {
  Loader2,
  FileText,
  AlertTriangle,
  Check,
  Image as ImageIcon,
  ArrowRight,
} from 'lucide-react';
import type { DiscoveredPage } from '../../../hooks/useRoaryImport';
import { brandColorSwatches } from './importBrandDisplay';

interface ImportScanProgressProps {
  sourceUrl: string;
  pages: DiscoveredPage[];
  assetsDiscovered: number;
  brandColors: Record<string, string> | null;
  brandFonts: string[];
  logoUrl: string | null;
  analyzing: boolean;
  selecting?: boolean;
  onCancel: () => void;
  onContinue?: (paths: string[]) => void;
}

export const ImportScanProgress: React.FC<ImportScanProgressProps> = ({
  sourceUrl,
  pages,
  assetsDiscovered,
  brandColors,
  brandFonts,
  logoUrl,
  analyzing,
  selecting,
  onCancel,
  onContinue,
}) => {
  const parsed = pages.filter(p => p.status === 'parsed').length;
  const failed = pages.filter(p => p.status === 'failed').length;
  const pending = pages.filter(p => p.status === 'pending');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selecting) return;
    setSelected(new Set(pending.map(p => p.path)));
    // Re-seed when the candidate list changes (homepage scan just finished).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecting, pending.map(p => p.path).join('|')]);

  const toggle = (path: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const headerLabel = selecting ? 'Choose pages' : analyzing ? 'Analyzing' : 'Scanning';

  return (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2 text-orange-400 mb-1">
          {!selecting && <Loader2 className="w-4 h-4 animate-spin" />}
          <span className="text-xs font-semibold uppercase tracking-wider">{headerLabel}</span>
        </div>
        <h2 className="text-lg font-semibold truncate">{sourceUrl}</h2>
        <p className="text-sm text-slate-400 mt-1">
          {selecting
            ? 'Home is included. Pick extra pages from the nav to import with it.'
            : `${parsed} parsed · ${pending.length} pending · ${failed} failed · ${assetsDiscovered} assets`}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {(brandColors || brandFonts.length > 0 || logoUrl) && (
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">
              Brand detected
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Detected logo"
                  className="h-8 w-auto object-contain bg-white rounded p-1"
                />
              )}
              {brandColorSwatches(brandColors).length > 0 && (
                <div className="flex items-center gap-1.5">
                  {brandColorSwatches(brandColors).map(([k, v]) => (
                    <div
                      key={k}
                      className="w-7 h-7 rounded-md border border-slate-700"
                      style={{ background: v }}
                      title={`${k}: ${v}`}
                    />
                  ))}
                </div>
              )}
              {brandFonts.length > 0 && (
                <div className="text-sm text-slate-300 flex flex-wrap gap-2">
                  {brandFonts.slice(0, 3).map(f => (
                    <span
                      key={f}
                      className="px-2 py-0.5 rounded bg-slate-700/80 text-slate-200 text-xs"
                      style={{ fontFamily: f }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-slate-400">Pages</div>
            {assetsDiscovered > 0 && (
              <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                <ImageIcon size={12} /> {assetsDiscovered}
              </div>
            )}
          </div>
          {pages.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Discovering pages…</p>
          ) : (
            <ul className="space-y-1.5">
              {pages
                .slice()
                .sort((a, b) => a.depth - b.depth || a.path.localeCompare(b.path))
                .map(p => (
                  <li
                    key={p.path}
                    className="flex items-center gap-3 px-3 py-2 rounded-md bg-slate-800/40 border border-slate-800"
                  >
                    {selecting && p.status === 'pending' ? (
                      <input
                        type="checkbox"
                        checked={selected.has(p.path)}
                        onChange={() => toggle(p.path)}
                        className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                        aria-label={`Import ${p.title || p.path}`}
                      />
                    ) : p.thumbnailBase64 ? (
                      <img
                        src={`data:image/jpeg;base64,${p.thumbnailBase64}`}
                        alt=""
                        className="w-12 h-9 object-cover rounded border border-slate-700 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-9 flex items-center justify-center rounded bg-slate-700/40 flex-shrink-0">
                        <FileText size={14} className="text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-200 truncate">
                        {p.title || p.path}
                        {p.status === 'parsed' && p.depth === 0 ? (
                          <span className="ml-2 text-xs text-orange-300 font-normal">Home</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-slate-500 truncate font-mono">{p.path}</div>
                    </div>
                    {!selecting && p.status === 'pending' && (
                      <Loader2 size={14} className="text-slate-500 animate-spin" />
                    )}
                    {p.status === 'parsed' && <Check size={14} className="text-green-400" />}
                    {p.status === 'failed' && (
                      <AlertTriangle
                        size={14}
                        className="text-amber-400"
                        aria-label={p.error || 'Failed'}
                      />
                    )}
                  </li>
                ))}
            </ul>
          )}
        </div>

        {analyzing && !selecting && (
          <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
              <span className="text-sm font-semibold text-orange-200">Building a plan</span>
            </div>
            <p className="text-xs text-orange-200/70">
              Mapping the page structure onto Compete Pages components…
            </p>
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-800 flex items-center justify-between gap-3">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        {selecting && (
          <button
            onClick={() => onContinue?.([...selected])}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white font-semibold transition-colors"
          >
            Continue
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
};
